import React, { useState, useEffect } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  addMonths,
  subMonths,
  getDay,
  startOfDay,
  isBefore,
} from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BookingModal from '../components/BookingModal'; // Assuming BookingModal is in the same directory or correctly imported

// MUI Components
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { red } from '@mui/material/colors';

function CalendarPage() {
  const { user, isAuthenticated, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [opcionesAdicionales, setOpcionesAdicionales] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState('');
  const [tiposReservaBase, setTiposReservaBase] = useState([]);
  const monthShown = startOfMonth(currentMonth);
  const currentRealMonth = startOfMonth(new Date());

  // Usuario normal: no puede ir a meses anteriores al mes actual.
  // Admin: puede siempre.
  const canGoPrev = isAdmin || !isBefore(subMonths(monthShown, 1), currentRealMonth);



  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!isAuthenticated) return;

      setCalendarLoading(true);
      setCalendarError('');
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;

        const [disponibilidadRes, opcionesRes, TipoReservas] = await Promise.all([
          api.get(`/Reservas/disponibilidad?year=${year}&month=${month}`),
          api.get('/Reservas/opciones'),
          api.get('/Reservas/tipos-reserva')
        ]);


        setDisponibilidad(disponibilidadRes.data);
        setOpcionesAdicionales(opcionesRes.data);
        setTiposReservaBase(TipoReservas.data);
      } catch (err) {
        console.error('Error fetching calendar data:', err.response?.data || err);
        setCalendarError('Error al cargar el calendario o las opciones. Intenta de nuevo.');
      } finally {
        setCalendarLoading(false);
      }
    };

    fetchCalendarData();
  }, [currentMonth, isAuthenticated]);

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const getDayClass = (day) => {
    // Si es un día pasado, siempre es gris claro.
    const todayStart = startOfDay(new Date());
    if (startOfDay(day) < todayStart) {
      return { backgroundColor: '#e0e0e0ff' };
    }

    const dayData = disponibilidad.find(d => d.dia === day.getDate());
    if (!dayData) return { backgroundColor: 'white' }; // Si no hay datos, es blanco.

    // Encuentra los turnos por su estado
    const medioDiaBloqueado = dayData.detallesReservas.find(r => r.turno === 'MedioDia' && r.esBloqueoAdmin);
    const nocheBloqueado = dayData.detallesReservas.find(r => r.turno === 'Noche' && r.esBloqueoAdmin);

    const medioDiaReservado = dayData.detallesReservas.find(r => r.turno === 'MedioDia' && !r.esBloqueoAdmin);
    const nocheReservado = dayData.detallesReservas.find(r => r.turno === 'Noche' && !r.esBloqueoAdmin);

    // Asigna los colores según la prioridad
    const colorMedioDia = medioDiaBloqueado ? '#dca652' : medioDiaReservado ? '#c5e5bfff' : 'white';
    const colorNoche = nocheBloqueado ? '#dca652' : nocheReservado ? '#c5e5bfff' : 'white';

    // Si hay al menos un turno con un color que no sea blanco, crea el gradiente.
    if (colorMedioDia !== 'white' || colorNoche !== 'white') {
      return {
        background: `linear-gradient(to bottom, ${colorMedioDia} 50%, ${colorNoche} 50%)`
      };
    }

    // Si ambos turnos están disponibles, el fondo es blanco.
    return { backgroundColor: 'white' };
  };

  const handleDayClick = (day) => {
    const today = startOfDay(new Date());
    const nextMonthLimit = addMonths(startOfMonth(new Date()), 2);

    const isPast = day < today;
    const isTooFuture = day >= nextMonthLimit;

    if ((!isAdmin && isPast) || isTooFuture) return;

    const dayData = disponibilidad.find(d => d.dia === day.getDate());
    setSelectedDate(day);
    setSelectedDayData(dayData);
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => startOfMonth(addMonths(prev, 1)));
  };

  const goToPreviousMonth = () => {
    if (!canGoPrev) return;
    setCurrentMonth(prev => startOfMonth(subMonths(prev, 1)));
  };



  const getEstadoTurnos = () => {
    if (!selectedDayData) return { medioDiaOcupado: false, nocheOcupado: false };
    const medioDiaReserva = selectedDayData.detallesReservas.find(r => r.turno == 'MedioDia' && !r.esBloqueoAdmin);
    const nocheReserva = selectedDayData.detallesReservas.find(r => r.turno == 'Noche' && !r.esBloqueoAdmin);
    const medioDiaBloqueado = selectedDayData.detallesReservas.find(r => r.turno === 'MedioDia' && r.esBloqueoAdmin);
    const nocheBloqueado = selectedDayData.detallesReservas.find(r => r.turno === 'Noche' && r.esBloqueoAdmin);

    return {
      medioDiaOcupado: (medioDiaReserva || medioDiaBloqueado),
      nocheOcupado: (nocheReserva || nocheBloqueado),
    };

  };

  if (authLoading || !isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Cargando autenticación...</Typography>
      </Box>
    );
  }




  // Calculate the number of empty cells at the beginning to align with Monday start
  const firstDayOfMonth = startOfMonth(currentMonth);
  const emptyCellsBeforeFirstDay = (getDay(firstDayOfMonth) + 6) % 7;

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: 'grey.50', minHeight: '100vh' }}>


      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} mt={12}>
        <Button
          variant="contained"
          onClick={goToPreviousMonth}
          disabled={!canGoPrev}
          startIcon={<NavigateBeforeIcon />}
        >
          Anterior
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
          {format(currentMonth, 'MMMM yyyy', { locale: es }).toUpperCase()}
        </Typography>
        <Button
          variant="contained"
          onClick={goToNextMonth}
          endIcon={<NavigateNextIcon />}
        >
          Siguiente
        </Button>
      </Box>

      {calendarLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2, color: 'text.secondary' }}>Cargando disponibilidad del calendario...</Typography>
        </Box>
      ) : calendarError ? (
        <Alert severity="error" sx={{ mt: 4 }}>
          {calendarError}
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)', // 👈 siempre 7 columnas
              columnGap: 1, // 👈 espacio horizontal entre columnas (días)
              mb: 2,
            }}
          >
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
              <Box
                key={day}
                sx={{
                  textAlign: 'center',
                  fontWeight: 'medium',
                  color: 'text.secondary',
                  borderBottom: '3px solid',
                  borderColor: 'grey.300',
                  py: 1,
                  pb: 2,
                }}
              >
                {day}
              </Box>
            ))}
          </Box>


          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)', // 7 columnas iguales
              gap: 1,
            }}
          >
            {/* Celdas vacías */}
            {Array.from({ length: emptyCellsBeforeFirstDay }).map((_, i) => (
              <Box
                key={`empty-${i}`}
                sx={{
                  height: 100,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              />
            ))}

            {/* Días del mes */}
            {daysInMonth.map((day) => {
              const dayData = disponibilidad.find(d => d.dia === day.getDate());
              const isCurrentDay = isToday(day);
              const todayStart = startOfDay(new Date());
              const nextMonthLimit = addMonths(startOfMonth(new Date()), 2); // mes actual + 1
              const isPastDay = startOfDay(day) < todayStart;
              const isFutureDisabled = startOfDay(day) >= nextMonthLimit;
              const isDisabled = (!isAdmin && isPastDay) || isFutureDisabled;

              const reservasOrdenadas = [...(dayData?.detallesReservas || [])].sort((a, b) => {
                if (a.turno === 'MedioDia' && b.turno === 'Noche') return -1;
                if (a.turno === 'Noche' && b.turno === 'MedioDia') return 1;
                return 0;
              });


              return (
                <Paper
                  key={day.toISOString()}
                  elevation={1}
                  onClick={() => !isDisabled && handleDayClick(day)}
                  sx={{
                    height: 100,
                    p: 1,
                    borderRadius: 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    position: 'relative', // 👈 IMPORTANTE para posicionar hijos absolutos
                    ...getDayClass(day),
                    ...(isCurrentDay && { border: '2px solid', borderColor: 'primary.main' }),
                    ...(isPastDay && { color: 'text.disabled' }),
                    '&:hover::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderRadius: 1,
                    },
                  }}
                >
                  {/* Número del día */}
                  <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                    {format(day, 'd')}
                  </Typography>

                  {/* Texto Medio Día - arriba derecha */}
                  {reservasOrdenadas
                    .filter(detalle => detalle.turno === 'MedioDia')
                    .map((detalle, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          top: 20,
                          right: 20,
                          fontSize: '1rem',
                          color: detalle.esBloqueoAdmin ? 'dark' : 'dark',
                          display: { xs: 'none', md: 'block' } // 👈 oculta en móviles

                        }}
                      >
                        {detalle.esBloqueoAdmin ? detalle.concepto : `Apto ${detalle.numeroApartamento}`}
                      </Typography>
                    ))}

                  {/* Texto Noche - abajo derecha */}
                  {reservasOrdenadas
                    .filter(detalle => detalle.turno === 'Noche')
                    .map((detalle, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: 20,
                          right: 20,
                          fontSize: '1rem',
                          color: detalle.esBloqueoAdmin ? 'dark' : 'dark',
                          display: { xs: 'none', md: 'block' } // 👈 oculta en móviles

                        }}
                      >
                        {detalle.esBloqueoAdmin ? detalle.concepto : `Apto ${detalle.numeroApartamento}`}
                      </Typography>
                    ))}
                </Paper>


              );
            })}
          </Box>
        </>
      )
      }

      {
        selectedDate && (
          <BookingModal
            selectedDate={selectedDate}
            dayData={selectedDayData}              // ✅ NUEVO
            onClose={() => setSelectedDate(null)}
            onReservaSuccess={() => setCurrentMonth(new Date(currentMonth))}
            opcionesAdicionales={opcionesAdicionales}
            estadoTurnos={getEstadoTurnos} // <-- acá va
            tiposReservaBase={tiposReservaBase}

          />
        )
      }
    </Container >
  );
}

export default CalendarPage;
